import { keepPreviousData, queryOptions, useInfiniteQuery, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  accountInfoSchema, activityDetailSchema, activityPageSchema, fitnessResponseSchema, planRangeSchema, powerCurveComparisonSchema, recommendationsSchema, settingsSchema,
  stravaStatusSchema, thresholdListSchema, thresholdSuggestionsSchema, weeklyZonesSchema, weekSnapshotSchema,
  type ThresholdKind, type ThresholdSport, type ThresholdSuggestions,
} from '@painpal/contracts';
import { apiJson } from '../lib/api';
import { connectedRecently, isImporting } from '../lib/strava';
import { invalidateTraining, mutationKeys, queryKeys, type PowerCurveWindow, type SportFilter, type ZoneSource } from './keys';
import { activitiesPath, activityPath, fitnessPath, planPath, powerCurvePath, RECOMMENDATIONS_PATH, THRESHOLD_SUGGESTIONS_PATH, thresholdsPath, weeklyZonesPath, weekPath } from './paths';

const FIRST_PAGE: string | null = null;

export function useActivities(sport: SportFilter) {
  return useInfiniteQuery({
    queryKey: queryKeys.activityList(sport),
    queryFn: ({ pageParam }) => apiJson(activitiesPath(sport, pageParam), activityPageSchema),
    initialPageParam: FIRST_PAGE,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    refetchInterval: ({ state }) => (state.data?.pages.some((page) => page.items.some((item) => item.status === 'pending')) ? 5000 : false),
  });
}

export function useActivity(id: string) {
  return useQuery({
    queryKey: queryKeys.activity(id),
    queryFn: () => apiJson(activityPath(id), activityDetailSchema),
    refetchInterval: ({ state }) => (state.data?.status === 'pending' ? 3000 : false),
  });
}

export const FITNESS_LIVE_MS = 30_000;
export const REBUILD_POLL_CAP_MS = 10 * 60_000;

export interface FitnessPolling {
  fitness: (rebuilding: boolean) => number | false;
  zones: () => number | false;
  rebuildTimedOut: boolean;
}

// Fitness and the weekly zones poll while a Strava import runs, or while the server rebuilds Fitness for at most
// REBUILD_POLL_CAP_MS in a row, so a rebuild that never settles can't poll forever. A new mount starts the window again.
// The zones follow the latest Fitness read: TanStack evaluates its refetchInterval before React re-renders,
// so a rebuild that starts mid-visit is already visible when the zones query re-reads its interval.
// rebuildTimedOut flips on a timer at the cap, because a stopped poll causes no re-render to show it.
export function useFitnessPolling(importing: boolean): FitnessPolling {
  const since = useRef<number | null>(null);
  const capTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [rebuildTimedOut, setRebuildTimedOut] = useState(false);
  useEffect(() => () => clearTimeout(capTimer.current), []);
  const intervals = useMemo(() => {
    const interval = (): number | false => {
      const rebuildLive = since.current !== null && Date.now() - since.current < REBUILD_POLL_CAP_MS;
      return importing || rebuildLive ? FITNESS_LIVE_MS : false;
    };
    const fitness = (rebuilding: boolean): number | false => {
      if (rebuilding && since.current === null) {
        since.current = Date.now();
        setRebuildTimedOut(false);
        capTimer.current = setTimeout(() => setRebuildTimedOut(true), REBUILD_POLL_CAP_MS);
      } else if (!rebuilding) {
        since.current = null;
        clearTimeout(capTimer.current);
      }
      return interval();
    };
    return { fitness, zones: interval };
  }, [importing]);
  return { ...intervals, rebuildTimedOut };
}

export function useFitness(from: string | null, to: string, interval: (rebuilding: boolean) => number | false) {
  return useQuery({
    queryKey: queryKeys.fitnessRange(from, to),
    queryFn: () => apiJson(fitnessPath(from, to), fitnessResponseSchema),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => interval(query.state.data?.rebuilding === true),
  });
}

// Fresh for a minute, so a prefetched previous week shows at once without a second fetch; invalidation still refetches.
const weekQuery = (start: string, sport: SportFilter) => queryOptions({
  queryKey: queryKeys.week(start, sport),
  queryFn: () => apiJson(weekPath(start, sport), weekSnapshotSchema),
  staleTime: 60_000,
});

export function useWeek(start: string, sport: SportFilter = 'all', interval: () => number | false = () => false) {
  return useQuery({
    ...weekQuery(start, sport),
    // Only a week change keeps the last week; another sport's figures would sit under this sport's wording.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[3] === sport ? previous : undefined),
    refetchInterval: interval,
  });
}

export function usePrefetchWeek(start: string | null, sport: SportFilter = 'all'): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (start !== null) void queryClient.prefetchQuery(weekQuery(start, sport));
  }, [queryClient, start, sport]);
}

// Fresh for a minute so paging back to a week shows it at once; plan edits and invalidateTraining still refetch.
export function usePlan(from: string, to: string) {
  return useQuery({
    queryKey: queryKeys.planRange(from, to),
    queryFn: () => apiJson(planPath(from, to), planRangeSchema),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useRecommendations() {
  return useQuery({ queryKey: queryKeys.recommendations, queryFn: () => apiJson(RECOMMENDATIONS_PATH, recommendationsSchema), staleTime: 60_000 });
}

export function useThresholds(kind: ThresholdKind = 'ftp', sport: ThresholdSport = 'ride') {
  return useQuery({
    queryKey: queryKeys.thresholdList(sport, kind),
    queryFn: () => apiJson(thresholdsPath(sport, kind), thresholdListSchema),
    refetchInterval: ({ state }) => (state.data?.recalculating ? 5000 : false),
  });
}

export function useStravaStatus() {
  return useQuery({
    queryKey: queryKeys.strava,
    queryFn: () => apiJson('/api/strava/status', stravaStatusSchema),
    refetchInterval: ({ state }) => (isImporting(state.data?.backfill) ? 15_000 : false),
  });
}

export function accountQueryOptions() {
  return queryOptions({ queryKey: queryKeys.account, queryFn: () => apiJson('/api/account', accountInfoSchema) });
}

export function useAccount() {
  return useQuery(accountQueryOptions());
}

export function usePowerCurve(id: string, window: PowerCurveWindow) {
  return useQuery({
    queryKey: queryKeys.powerCurve(id, window),
    queryFn: () => apiJson(powerCurvePath(id, window), powerCurveComparisonSchema),
    placeholderData: keepPreviousData,
  });
}

export function useWeeklyZones(
  from: string | null, to: string, source: ZoneSource, sport: SportFilter = 'all', interval: () => number | false = () => false,
) {
  return useQuery({
    queryKey: queryKeys.weeklyZones(from, to, source, sport),
    queryFn: () => apiJson(weeklyZonesPath(from, to, source, sport), weeklyZonesSchema),
    // Only a range change keeps the old weeks; another source's or sport's weeks would be labelled wrongly.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[4] === source && previousQuery.queryKey[5] === sport ? previous : undefined),
    refetchInterval: interval,
  });
}

export function useSettings() {
  return useQuery({ queryKey: queryKeys.settings, queryFn: () => apiJson('/api/settings', settingsSchema) });
}

export const RECONNECT_POLL_MS = 5000;

// A read that auto-applied a first Max HR history refreshes the thresholds and what derives from them. It runs as a Max
// HR threshold-change mutation, like an accept, so the recalculation banner words itself for HR zones. This query is
// refetched with them only so autoApplied clears: the next read reports false (once per kind), so it cannot loop.
// Right after a Strava connect, the FTP check the new grant queued may not have run yet, so a reconnect prompt is
// re-read until it clears; that check may also have applied a first FTP, hence the refresh when it does. Both refreshes
// wait a tick so they land after this fetch settles (invalidating mid-fetch would cancel it).
export function useThresholdSuggestions() {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.thresholdSuggestions,
    queryFn: async () => {
      const before = queryClient.getQueryData<ThresholdSuggestions>(queryKeys.thresholdSuggestions);
      const suggestions = await apiJson(THRESHOLD_SUGGESTIONS_PATH, thresholdSuggestionsSchema);
      if (suggestions.autoApplied) setTimeout(() => void recordAutoApplied(queryClient), 0);
      else if (before?.ftp.state === 'reconnect' && suggestions.ftp.state !== 'reconnect') {
        setTimeout(() => void invalidateTraining(queryClient), 0);
      }
      return suggestions;
    },
    refetchInterval: ({ state }) => (state.data?.ftp.state === 'reconnect' && connectedRecently() ? RECONNECT_POLL_MS : false),
  });
}

function recordAutoApplied(queryClient: QueryClient): Promise<unknown> {
  const mutation = queryClient.getMutationCache().build(queryClient, {
    mutationKey: mutationKeys.thresholdChange('ride', 'max_hr'),
    mutationFn: () => invalidateTraining(queryClient),
  });
  return mutation.execute(undefined);
}
